"use client";

import { useEffect, useRef, useState } from "react";
import { EllipsisVertical } from "lucide-react";

/**
 * Shared config-driven object action menu.
 * Items should be prepared by the container so this component stays presentational.
 */
export default function ObjectActionMenu({
  label = "Actions",
  items = [],
  menuClassName = "",
  triggerClassName = "",
  align = "right",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const enabledItems = items.filter((item) => item && item.hidden !== true);

  useEffect(() => {
    function onDoc(event) {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (enabledItems.length === 0) return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
        className={`btn-halo-icon rounded-full text-[var(--muted)] hover:text-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--card)] ${triggerClassName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
      >
        <span className="icon-halo">
          <EllipsisVertical className="h-5 w-5" />
        </span>
      </button>

      {open ? (
        <div
          role="menu"
          className={`absolute z-50 mt-1 min-w-44 overflow-hidden rounded-md border border-[var(--border)] bg-[var(--card)] shadow-md ${
            align === "left" ? "left-0" : "right-0"
          } ${menuClassName}`}
          onClick={(event) => event.stopPropagation()}
        >
          {enabledItems.map((item, index) => {
            const Icon = item.icon;
            const toneClass =
              item.tone === "danger"
                ? "text-red-500 hover:bg-red-500/10"
                : "text-[var(--foreground)] hover:bg-[var(--surface-2)]";
            return (
              <button
                key={`${item.label}-${index}`}
                role="menuitem"
                type="button"
                className={`flex w-full items-center gap-2 px-3 py-2 text-left ${toneClass}`}
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onSelect?.();
                }}
              >
                {Icon ? <Icon className="h-4 w-4 shrink-0" /> : null}
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
